import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  generateUtilityClasses,
} from '@mui/material'
import { styled } from '@mui/system'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { storageGet, storageSet } from '../../../../utils/storage'
import { useDispatch } from 'react-redux'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../utils/sorting'
import ConfirmDialog from '../../../../components/ConfirmDialog'
import { UspConfigurationCreditCardTypeViewResult } from '../../../../models'
import { useDeleteCreditCardTypeMutation } from '../../../../services'
import { enqueueNotification } from '../../../../redux/slices/notificationsSlice'
import { CustomCircular } from '../../../../ui'
import { usePopover } from '../../../../components/custom-popover'
import Iconify from '../../../../components/iconify'
import CustomPopover from '../../../../components/custom-popover/custom-popover'
import Label from '../../../../components/label'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'loading', 'tableHead', 'tableCellName', 'inactiveItem', 'lastColumn'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.lastColumn}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
    width: '80px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
}))

interface Props {
  data: UspConfigurationCreditCardTypeViewResult[]
  loading: boolean
  setData: Dispatch<SetStateAction<UspConfigurationCreditCardTypeViewResult[]>>
  setOpenAddEditModal: Dispatch<SetStateAction<boolean>>
  setType: Dispatch<SetStateAction<string>>
  setRowData: Dispatch<SetStateAction<UspConfigurationCreditCardTypeViewResult>>
}
const PaymentCreditCardTypesTable = ({ data, loading, setData, setOpenAddEditModal, setRowData, setType }: Props) => {
  const [deleteCreditCardType, { isSuccess: deleteIsSuccess }] = useDeleteCreditCardTypeMutation()
  const initialOrder = (storageGet('credit_card_type_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('credit_card_type_order_by') || 'CardType') as keyof UspConfigurationCreditCardTypeViewResult
  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = useState<keyof UspConfigurationCreditCardTypeViewResult>(initialOrderBy)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{ creditCardTypeId: string } | undefined>(undefined)
  const dispatch = useDispatch()
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspConfigurationCreditCardTypeViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('credit_card_type_order', isAsc ? 'desc' : 'asc')
    storageSet('credit_card_type_order_by', property)
  }

  const createSortHandler = (property: keyof UspConfigurationCreditCardTypeViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = async (): Promise<void> => {
    if (deleteConfirmDialog?.creditCardTypeId) {
      await deleteCreditCardType(deleteConfirmDialog)
    }
    setDeleteConfirmDialog(undefined)
  }

  const handleOpen = () => {
    setType('edit')
    setOpenAddEditModal(true)
  }

  useEffect(() => {
    if (deleteConfirmDialog?.creditCardTypeId) {
      if (deleteIsSuccess) {
        let updatedData: UspConfigurationCreditCardTypeViewResult[]
        updatedData = data.map((item) => (item.ID?.toString() === deleteConfirmDialog.creditCardTypeId ? { ...item, IsActive: false } : item))
        setData(updatedData)
        dispatch(enqueueNotification({ message: 'Success' }))
      }
    }
  }, [deleteIsSuccess])
  return (
    <>
      <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 118px)' }} className={classes.root}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'CardType' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'CardType'}
                  direction={orderBy === 'CardType' ? order : 'asc'}
                  onClick={createSortHandler('CardType')}
                >
                  Name
                  {orderBy === 'CardType' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'CardShortCode' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'CardShortCode'}
                  direction={orderBy === 'CardShortCode' ? order : 'asc'}
                  onClick={createSortHandler('CardShortCode')}
                >
                  Short Code
                  {orderBy === 'CardShortCode' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsAccepted' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsAccepted'}
                  direction={orderBy === 'IsAccepted' ? order : 'asc'}
                  onClick={createSortHandler('IsAccepted')}
                >
                  Accepted
                  {orderBy === 'IsAccepted' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsProcessInSystem' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsProcessInSystem'}
                  direction={orderBy === 'IsProcessInSystem' ? order : 'asc'}
                  onClick={createSortHandler('IsProcessInSystem')}
                >
                  Process in System
                  {orderBy === 'IsProcessInSystem' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.lastColumn} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.lastColumn} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), data as any)?.map((item) => (
              <TableRow key={item.ID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell
                  className={classes.tableCellName}
                  onClick={() => {
                    handleOpen()
                    setRowData(item as any)
                  }}
                >
                  {item.CardType}
                </TableCell>
                <TableCell>{item?.CardShortCode}</TableCell>
                <TableCell>{item?.IsAccepted ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsProcessInSystem ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                </TableCell>
                <TableCell align={'right'}>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setSelected(item)
                      popover.onOpen(event)
                    }}
                  >
                    <Iconify icon='eva:more-vertical-fill' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomCircular open={loading} />
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Are you sure you wish to delete?'}
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
        <MenuItem
          onClick={(e) => {
            handleOpen()
            setRowData(selected as any)
            popover.onClose()
          }}
        >
          <Iconify icon='solar:pen-bold' />
          Edit
        </MenuItem>
        <MenuItem
          disabled={selected?.IsActive ? false : true}
          onClick={() => {
            setDeleteConfirmDialog({ creditCardTypeId: selected.ID?.toString() as string })

            popover.onClose()
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon='solar:trash-bin-trash-bold' />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  )
}

export default PaymentCreditCardTypesTable
